import { IDyLabel } from './dyAbstractLabel';
import { RefSelector } from './componentAnnotations';
import { DyAbstractField as DyAbstractField, FieldElement } from './dyAbstractField';
import { setDisabled, setElementWidth, addCssClass, addOrRemoveAttribute } from '../utils/dom';
import { setAriaLabelledBy, setAriaLabel } from '../utils/aria';
import { exists } from '../utils/generic';

export interface IInputField extends IDyLabel {
  value?: any;
  width?: number;
}

export abstract class DyAbstractInputField<TElement extends FieldElement, TValue, TConfig extends IInputField = IInputField>
  extends DyAbstractField<TValue, TConfig> {
  @RefSelector('eLabel') protected readonly eLabel: HTMLElement;
  @RefSelector('eWrapper') protected readonly eWrapper: HTMLElement;
  @RefSelector('eInput') protected readonly eInput: TElement;

  constructor(config?: TConfig, className?: string, private readonly inputType: string | null = 'text', private readonly displayFieldTag = 'input') {
    super(config, /* html */`
            <div role="presentation">
                <div ref="eLabel" class="dy-input-field-label"></div>
                <div ref="eWrapper" class="dy-wrapper dy-input-wrapper" role="presentation">
                    <${displayFieldTag} ref="eInput" class="dy-input-field-input"></${displayFieldTag}>
                </div>
            </div>`, className);
  }

  protected postConstruct() {
    super.postConstruct();
    this.setInputType();

    addCssClass(this.eLabel, `${this.className}-label`);
    addCssClass(this.eWrapper, `${this.className}-input-wrapper`);
    addCssClass(this.eInput, `${this.className}-input`);
    addCssClass(this.getGui(), 'dy-input-field');

    this.eInput.id = this.eInput.id || `dy-${this.getCompId()}-input`;

    const { width, value } = this.config;

    if (width != null) {
      this.setWidth(width);
    }

    if (value != null) {
      this.setValue(value);
    }

    this.addInputListeners();
  }

  protected refreshLabel() {
    if (exists(this.getLabel())) {
      setAriaLabelledBy(this.eInput, this.getLabelId());
    } else {
      this.eInput.removeAttribute('aria-labelledby');
    }

    super.refreshLabel();
  }

  protected addInputListeners() {
    this.addManagedListener(this.eInput, 'input', e => this.setValue(e.target.value));
  }

  private setInputType() {
    if (this.displayFieldTag === 'input') {
      this.eInput.setAttribute('type', this.inputType!);
    }
  }

  public getInputElement(): TElement {
    return this.eInput;
  }

  public setInputWidth(width: number | 'flex'): this {
    setElementWidth(this.eWrapper, width);

    return this;
  }

  public setInputName(name: string): this {
    this.getInputElement().setAttribute('name', name);

    return this;
  }

  public getFocusableElement(): HTMLElement {
    return this.eInput;
  }

  public setMaxLength(length: number): this {
    const eInput = this.eInput as HTMLInputElement | HTMLTextAreaElement;
    eInput.maxLength = length;

    return this;
  }

  public setInputPlaceholder(placeholder: string): this {
    addOrRemoveAttribute(this.eInput, 'placeholder', placeholder);

    return this;
  }

  public setInputAriaLabel(label: string): this {
    setAriaLabel(this.eInput, label);

    return this;
  }

  public setDisabled(disabled: boolean): this {
    setDisabled(this.eInput, disabled);

    return super.setDisabled(disabled);
  }
}
